/**
 * Celebrations Hook
 * Handles confetti animation and celebration horn audio
 * IMPORTANT: Preserve exact physics and audio algorithms
 */

import { useRef, useCallback, useEffect } from 'react'
import { CONFETTI, HORN } from '@config/constants'

interface ConfettiParticle {
  x: number
  y: number
  size: number
  color: string
  vx: number
  vy: number
  rotation: number
  rotationSpeed: number
}

interface CelebrationState {
  confettiCanvas: HTMLCanvasElement | null
  confettiCtx: CanvasRenderingContext2D | null
  confettiParticles: ConfettiParticle[]
  confettiFrameId: number | null
  confettiTimeoutId: number | null
  audioContext: AudioContext | null
}

export function useCelebrations() {
  const stateRef = useRef<CelebrationState>({
    confettiCanvas: null,
    confettiCtx: null,
    confettiParticles: [],
    confettiFrameId: null,
    confettiTimeoutId: null,
    audioContext: null,
  })

  const createConfettiParticles = useCallback((width: number, height: number): ConfettiParticle[] => {
    const colors = ['#e74c3c', '#ff7f00', '#ffff00', '#27ae60', '#3498db', '#9b59b6']
    const particles: ConfettiParticle[] = []
    const count = Math.min(CONFETTI.MAX_PARTICLES, Math.floor(width / CONFETTI.PARTICLE_DENSITY))
    for (let i = 0; i < count; i += 1) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height - height / 2,
        size: 6 + Math.random() * 6,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 2,
        vy: 1 + Math.random() * 2,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
      })
    }
    return particles
  }, [])

  const cleanupConfettiCanvas = useCallback(() => {
    const state = stateRef.current
    if (state.confettiFrameId) {
      cancelAnimationFrame(state.confettiFrameId)
      state.confettiFrameId = null
    }
    if (state.confettiTimeoutId) {
      clearTimeout(state.confettiTimeoutId)
      state.confettiTimeoutId = null
    }
    if (state.confettiCanvas && state.confettiCanvas.parentNode) {
      state.confettiCanvas.parentNode.removeChild(state.confettiCanvas)
    }
    state.confettiCanvas = null
    state.confettiCtx = null
    state.confettiParticles = []
  }, [])

  const launchConfettiBurst = useCallback(
    (durationMs: number = CONFETTI.DEFAULT_DURATION) => {
      cleanupConfettiCanvas()
      const canvas = document.createElement('canvas')
      canvas.className = 'confetti-canvas'
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      canvas.setAttribute('aria-hidden', 'true')
      document.body.appendChild(canvas)

      const state = stateRef.current
      state.confettiCanvas = canvas
      state.confettiCtx = canvas.getContext('2d')
      state.confettiParticles = createConfettiParticles(canvas.width, canvas.height)

      const animate = () => {
        const state = stateRef.current
        if (!state.confettiCtx) {
          return
        }
        const ctx = state.confettiCtx
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        state.confettiParticles.forEach(particle => {
          ctx.save()
          ctx.fillStyle = particle.color
          ctx.translate(particle.x, particle.y)
          ctx.rotate(particle.rotation)
          ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * 0.6)
          ctx.restore()
          particle.x += particle.vx
          particle.y += particle.vy
          particle.rotation += particle.rotationSpeed
          particle.vy += CONFETTI.GRAVITY
          if (particle.y > canvas.height + CONFETTI.RESPAWN_OFFSET) {
            particle.y = -CONFETTI.RESPAWN_OFFSET
            particle.x = Math.random() * canvas.width
          }
        })
        state.confettiFrameId = requestAnimationFrame(animate)
      }

      state.confettiFrameId = requestAnimationFrame(animate)
      state.confettiTimeoutId = window.setTimeout(() => {
        cleanupConfettiCanvas()
      }, durationMs)
    },
    [cleanupConfettiCanvas, createConfettiParticles]
  )

  const playCelebrationHorn = useCallback(() => {
    const AudioCtx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) {
      return
    }

    const state = stateRef.current
    if (!state.audioContext) {
      state.audioContext = new AudioCtx()
    }

    const ctx = state.audioContext
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {})
    }

    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'triangle'
    osc.frequency.setValueAtTime(HORN.START_FREQUENCY, now)
    osc.frequency.exponentialRampToValueAtTime(HORN.PEAK_FREQUENCY, now + HORN.PEAK_TIME)
    osc.frequency.exponentialRampToValueAtTime(HORN.END_FREQUENCY, now + HORN.DECAY_TIME)

    gain.gain.setValueAtTime(HORN.INITIAL_GAIN, now)
    gain.gain.exponentialRampToValueAtTime(HORN.PEAK_GAIN, now + HORN.ATTACK_TIME)
    gain.gain.exponentialRampToValueAtTime(HORN.INITIAL_GAIN, now + HORN.RELEASE_TIME)

    osc.connect(gain).connect(ctx.destination)
    osc.start(now)
    osc.stop(now + HORN.RELEASE_TIME)
  }, [])

  const triggerCelebration = useCallback(
    ({ confettiEnabled = true, streak = 0 }: { confettiEnabled?: boolean; streak?: number } = {}) => {
      if (!confettiEnabled) {
        return
      }
      const durationMs: number =
        CONFETTI.MIN_DURATION +
        Math.min(Math.log2(streak + 1) * CONFETTI.STREAK_DURATION_FACTOR, CONFETTI.MAX_ADDITIONAL_DURATION)
      launchConfettiBurst(durationMs)
    },
    [launchConfettiBurst]
  )

  const cleanup = useCallback(() => {
    cleanupConfettiCanvas()
    const state = stateRef.current
    if (state.audioContext) {
      state.audioContext.close().catch(() => {})
      state.audioContext = null
    }
  }, [cleanupConfettiCanvas])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    triggerCelebration,
    launchConfettiBurst,
    playCelebrationHorn,
    cleanup,
  }
}
